import React from 'react';
import { Link } from 'react-router-dom';

export default function Expansion() {
  return (
    <main style={{ display: 'block' }}>
      <section className="page-hero">
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <span className="eyebrow">Platform Expansion Strategy</span>
          <h1 className="hero-headline" style={{ maxWidth: 900, marginBottom: 24 }}>
            From leukemia to a multi-cancer early detection franchise.
          </h1>
          <p className="body-lg" style={{ maxWidth: 720, marginBottom: 16 }}>
            The LENA Platform™ is architected as a cancer-agnostic epigenetic inference engine. Leukemia is the validation beachhead. The underlying biology — abnormal methylation drift preceding clinical diagnosis — is a universal hallmark of cancer development across tissue types.
          </p>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginTop: 8 }}>
            This page describes Alercell's scientific and product roadmap. It does not constitute an offer to sell or solicitation to buy securities.
          </p>
        </div>
      </section>

      <section style={{ background: 'var(--bg-surface)', padding: '60px 0', borderBottom: '1px solid var(--border)' }}>
        <div className="container">
          <span className="eyebrow">Platform Expansion Roadmap</span>
          <h2 className="section-headline" style={{ marginBottom: 32 }}>
            LENA Platform: Scaling from Leukemia to a Multi-Cancer Early Detection Franchise
          </h2>
          <div style={{ borderRadius: 'var(--radius)', overflow: 'hidden', border: '1px solid var(--border)', marginTop: 24 }}>
  <img
    src="/lena-expansion-roadmap.jpg"
    alt="LENA Platform: Scaling from Leukemia to a Multi-Cancer Early Detection Franchise"
    style={{ width: '100%', height: 'auto', display: 'block' }}
  />
</div>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginTop: 16, textAlign: 'center' }}>
            888,000+ annual patient opportunities across five major cancer indications by 2028. 12–48 month detection lead-time. One platform. No re-engineering.
          </p>
        </div>
      </section>

      <section className="section" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <span className="eyebrow">The Core Principle</span>
          <h2 className="section-headline" style={{ maxWidth: 800, marginBottom: 20 }}>
            The LENA Platform is cancer-type agnostic.
          </h2>
          <div className="cyan-line" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 48 }} className="bio-grid">
            <div>
              <p className="body-md" style={{ marginBottom: 20 }}>
                The LENA Code® normalization engine and LENA Score™ predictive algorithms operate on a fundamental principle that transcends specific cancer histology: abnormal epigenetic drift in the pre-malignant state is a universal signature of clonal evolution across all human cancers.
              </p>
              <p className="body-md">
                Leukemia is the starting point because liquid biopsy access makes blood-based biomarkers more tractable, clonal evolution is rapid and pronounced, clinical unmet need is high, and concentrated high-risk populations enable fast prospective validation. But the underlying biology is not unique to hematologic malignancies.
              </p>
            </div>
            <div>
              {[
                { title: 'LENA Code® Normalization Engine', body: 'Agnostic batch correction and cell-composition adjustment. Applicable to any tissue type or cancer model. Handles both solid-tissue methylation and liquid-biopsy approaches.' },
                { title: 'LENA Score™ Predictive Algorithms', body: 'Machine-learning framework trained on epigenetic drift patterns. Adapts to tissue-specific CpG sites and methylation kinetics. Non-linear risk modeling accounts for cancer-type-specific progression rates.' },
                { title: 'EDU (Epigenetic Drift Unit) Framework', body: 'Quantifies methylation velocity independent of cancer type. Validated across leukemic progression; directly transferable to solid tumors. Enables prospective early-detection claims across indications.' },
              ].map((item, i) => (
                <div key={i} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderLeft: '3px solid var(--accent-cyan)', borderRadius: '0 var(--radius) var(--radius) 0', padding: '20px 24px', marginBottom: 16 }}>
                  <h4 style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>{item.title}</h4>
                  <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>{item.body}</p>
                </div>
              ))}
            </div>
          </div>
          <style>{`@media(max-width:768px){.bio-grid{grid-template-columns:1fr!important;}}`}</style>
        </div>
      </section>

      <section className="section section-surface">
        <div className="container">
          <span className="eyebrow">Development Pipeline</span>
          <h2 className="section-headline" style={{ marginBottom: 8 }}>Three phases. Five cancers. One platform.</h2>
          <div className="cyan-line" />
          {[
            {
              period: '2026–2027', phase: 'The Leukemia Beachhead', color: '#00AEEF',
              items: [
                'Complete prospective validation of LENA Platform for AML, ALL, CLL/SLL',
                'Achieve FDA 510(k) clearance for early leukemia detection (LENA-RX™)',
                'Establish clinical utility evidence, reimbursement codes, and commercial partnerships',
                'Build reference methylation databases for leukemic progression patterns',
              ],
              note: 'Leukemia success validates the core technology and establishes the regulatory predicate device for all subsequent indications.',
            },
            {
              period: '2027', phase: 'Lymphoma Scale-Up', color: '#00C896',
              items: [
                'Transitional indication: Lymphomas share methylation-driven clonal evolution with leukemias',
                'Leverage existing LENA Code® normalization engine; adapt LENA Score™ for lymphoma-specific CpG sites',
                'Prospective validation study (n=400–600 high-risk patients) concurrent with leukemia FDA review',
                'Expected commercialization: Late 2027',
              ],
              note: 'Rationale: Lymphomas and leukemias share hematopoietic developmental biology. Proves multi-indication scalability before entering solid tumor markets.',
            },
            {
              period: '2028', phase: 'Solid Tumor Expansion', color: '#FF8C42',
              items: [
                'Breast Cancer: High-risk women (BRCA1/BRCA2, dense breast tissue) — 18–36 month detection window',
                'Colorectal Cancer: Lynch syndrome, FAP, high-risk IBD — 24–48 month detection window',
                'Prostate Cancer: Men >55 with elevated PSA or genetic risk — 12–24 month detection window',
              ],
              note: 'Breast + Colon + Prostate represent 740,000+ additional annual diagnoses in the U.S. alone.',
            },
          ].map((phase, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 40, marginBottom: 40, paddingBottom: 40, borderBottom: i < 2 ? '1px solid var(--border)' : 'none' }} className="timeline-phase">
              <div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 800, color: phase.color, lineHeight: 1 }}>{phase.period}</div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginTop: 6 }}>{phase.phase}</div>
              </div>
              <div>
                <ul style={{ paddingLeft: 20, marginBottom: 16 }}>
                  {phase.items.map((item, j) => (
                    <li key={j} style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 8 }}>{item}</li>
                  ))}
                </ul>
                <div style={{ background: 'var(--bg-card)', border: `1px solid ${phase.color}33`, borderRadius: 'var(--radius-sm)', padding: '12px 16px' }}>
                  <p style={{ fontSize: 13, color: 'var(--text-secondary)', fontStyle: 'italic' }}>{phase.note}</p>
                </div>
              </div>
            </div>
          ))}
          <style>{`@media(max-width:768px){.timeline-phase{grid-template-columns:1fr!important;}}`}</style>
        </div>
      </section>

      <section className="section" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <span className="eyebrow">Scalability Without Technology Reinvention</span>
          <h2 className="section-headline" style={{ maxWidth: 800, marginBottom: 20 }}>
            One platform. Five cancers. 888,000+ annual patient opportunities by 2028.
          </h2>
          <div className="cyan-line" />
          <div className="metric-grid" style={{ marginBottom: 48 }}>
            {[
              { value: '58K', label: 'Leukemia / MDS annual US diagnoses' },
              { value: '90K', label: 'Lymphoma annual US diagnoses' },
              { value: '300K', label: 'Breast cancer annual US diagnoses' },
              { value: '150K', label: 'Colorectal cancer annual US diagnoses' },
              { value: '290K', label: 'Prostate cancer annual US diagnoses' },
            ].map((m, i) => (
              <div key={i} className="metric-item">
                <span className="metric-value">{m.value}</span>
                <p className="metric-label">{m.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section section-surface">
        <div className="container cta-banner">
          <h2 className="section-headline">Interested in the full expansion data package?</h2>
          <p className="body-lg">Detailed validation protocols, TAM analysis, and regulatory pathway documentation are available to qualified investors through our materials package.</p>
          <div className="cta-buttons">
            <Link to="/investors#materials-form" className="btn btn-primary">Request Investor Materials</Link>
            <Link to="/platform" className="btn btn-outline">View the Platform</Link>
          </div>
          <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 24, fontFamily: 'var(--font-mono)' }}>
            This page describes Alercell's scientific and product development roadmap. Nothing on this page constitutes an offer to sell or a solicitation of an offer to buy any security. All forward-looking statements involve risks and uncertainties.
          </p>
        </div>
      </section>
    </main>
  );
}
